import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import React, { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@platform/browser-logger";

import { QuotesSbsDeletePlanGroupDialog } from "../ContextMenu/QuotesSbsDeletePlanGroupDialog";
import { RequoteActionDialog } from "../ContextMenu/RequoteActionDialog";
import {
  useDuplicateProposalCoverage,
  useSuppressPadSlots,
} from "../../../../api/proposalPlanGroupMutations";
import {
  useDisregardProposal,
  usePromoteProposal,
  useReplaceProposal,
  useRestoreProposalPlanGroup,
} from "../../../../api/requoteMutations";
import { proposalQueryKeys } from "../../../../api/useProposals";

import type { QuoteVersionState } from "./RequoteBadge";
import type { RequoteAction } from "../ContextMenu/RequoteActionDialog";
import type { TooltipProps } from "@mui/material/Tooltip";
import type { PlaceholderMaterializeTarget } from "../../../../adapters/proposalToGrid";
import type {
  MovePlanTarget,
  PasteTarget,
} from "../../utilities/buildCarrierGroupedColumns";
import type { CustomHeaderGroupProps } from "ag-grid-react";

// AG Grid's DragService intercepts mouseover events on header cells, preventing
// the browser's native `title` attribute from triggering a tooltip. We replicate
// AG Grid's themeQuartz tooltip appearance so this is visually consistent with
// the grid's native cell tooltips.
const AgGridTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f8f9fa",
    color: "#181d1f",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 0,
    fontWeight: 400,
    lineHeight: 1.43,
    boxShadow: "0 0 16px 0 rgba(0, 0, 0, 0.15)",
    padding: "4px",
  },
});

// buildPendingRequoteMenuItems builds a pending requote column's menu: Replace
// / Disregard. A pending requote is always mapped by this point — an
// unmapped group's isPendingRequote is forced false upstream regardless of its
// owning proposal's state, since it has no requested-plan link for Replace to
// promote within (see buildCarrierHeaderParams). Extracted so the component's
// render logic doesn't carry this branching itself.
const buildPendingRequoteMenuItems = ({
  onReplace,
  onDisregard,
}: {
  onReplace: () => void;
  onDisregard: () => void;
}): React.JSX.Element[] => [
  <MenuItem key="replace" onClick={onReplace}>
    <ListItemIcon>
      <SwapHorizIcon color="action" fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Replace the Active Quote" />
  </MenuItem>,
  <MenuItem key="disregard" onClick={onDisregard}>
    <ListItemIcon>
      <BlockIcon color="action" fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Disregard this Quote" />
  </MenuItem>,
];

// ag-grid types headerGroupComponentParams as `any`; this is the shape
// buildPlanGroupColumns actually sets, so the reads below are type-checked.
interface CarrierGroupHeaderParams {
  planGroupIds?: string[];
  carrierName?: string;
  rfpId?: string;
  placeholderDismissTargets?: PlaceholderMaterializeTarget[];
  padDismissGroupIds?: string[];
  moveTarget?: MovePlanTarget;
  onMovePlan?: (target: MovePlanTarget) => void;
  pasteTargets?: PasteTarget[];
  onPastePlan?: (proposalPlanGroupId: string, targetPosition: number) => void;
  proposalId?: string;
  quoteVersion?: number;
  receivedAt?: string;
  isPendingRequote?: boolean;
  isHistorical?: boolean;
  /**
   * A quote the analyst deleted, surfaced in Quote History (CD-89 AC1). Its only action is
   * Restore, which clears the deletion without promoting: the standard plan actions would
   * edit a quote that isn't on the board, and Replace would have to restore and promote
   * in one step, which the API doesn't do.
   */
  isDeleted?: boolean;
  /** Which quote this column holds, resolved by the column builder (ADR-031). */
  versionState?: QuoteVersionState;
  isSingularPlan?: boolean;
}

/**
 * Applies the defaults and the proposal-id guard to the params ag-grid hands us.
 *
 * A pending requote's kebab offers only Replace / Disregard, a historical (actioned,
 * superseded) version's offers only Replace the Active Quote (CD-89 AC7, same
 * promote-to-active mutation as Replace), a deleted one offers only Restore, and every
 * other column keeps the standard plan actions (ADR-031). Each of those states needs a
 * proposalId to act on, so a column missing one falls back to the standard actions.
 */
const readHeaderParams = (
  raw: CarrierGroupHeaderParams,
): Omit<
  CarrierGroupHeaderParams,
  "carrierName" | "rfpId" | "proposalId" | "placeholderDismissTargets"
> & {
  carrierName: string;
  rfpId: string;
  proposalId: string;
  placeholderDismissTargets: PlaceholderMaterializeTarget[];
  isPendingRequote: boolean;
  isHistorical: boolean;
  isDeleted: boolean;
} => {
  const proposalId = raw.proposalId ?? "";

  return {
    ...raw,
    carrierName: raw.carrierName ?? "",
    rfpId: raw.rfpId ?? "",
    proposalId,
    placeholderDismissTargets: raw.placeholderDismissTargets ?? [],
    isPendingRequote: Boolean(raw.isPendingRequote && proposalId),
    isHistorical: Boolean(raw.isHistorical && proposalId),
    isDeleted: Boolean(raw.isDeleted && proposalId),
  };
};

const CarrierGroupHeader = (
  props: CustomHeaderGroupProps,
): React.JSX.Element => {
  const queryClient = useQueryClient();
  const duplicateCoverage = useDuplicateProposalCoverage();
  const suppressPadSlots = useSuppressPadSlots();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [requoteDialog, setRequoteDialog] = useState<RequoteAction | null>(
    null,
  );
  const colGroupDef = props.columnGroup
    .getProvidedColumnGroup()
    .getColGroupDef();
  const headerTooltip = colGroupDef?.headerTooltip;
  const {
    carrierName,
    rfpId,
    placeholderDismissTargets,
    padDismissGroupIds: rawPadDismissGroupIds,
    moveTarget,
    onMovePlan,
    pasteTargets: rawPasteTargets,
    onPastePlan,
    planGroupIds: rawPlanGroupIds,
    quoteVersion,
    isPendingRequote,
    isHistorical,
    isDeleted,
    isSingularPlan,
  } = readHeaderParams(
    (colGroupDef?.headerGroupComponentParams ?? {}) as CarrierGroupHeaderParams,
  );

  // Memoized so onDuplicate's useCallback dependency stays referentially stable.
  const planGroupIds = useMemo(() => rawPlanGroupIds ?? [], [rawPlanGroupIds]);
  const padDismissGroupIds = useMemo(
    () => rawPadDismissGroupIds ?? [],
    [rawPadDismissGroupIds],
  );
  const pasteTargets = useMemo(() => rawPasteTargets ?? [], [rawPasteTargets]);

  // Own the requote mutations here, not in the dialog: the action rebuilds the
  // grid and tears the dialog down, so a dialog-owned mutation would lose its
  // success handler (the query invalidation) before the server responds. This
  // header stays mounted until the post-invalidation rebuild.
  const replaceProposal = useReplaceProposal(rfpId);
  const disregardProposal = useDisregardProposal(rfpId);
  const promoteProposal = usePromoteProposal(rfpId);
  const restoreProposal = useRestoreProposalPlanGroup(rfpId);

  const onRequoteConfirm = useCallback(() => {
    if (!requoteDialog) return;
    const subject =
      quoteVersion == null
        ? `${carrierName}'s quote`
        : `${carrierName}'s V${quoteVersion} quote`;

    // Restore resolves its own wording from the response: it never promotes, so whether
    // the quote lands back on the board or stays in Quote History depends on a Replace
    // that may have superseded it while it was deleted. Saying "restored" flatly would
    // send the analyst looking for a column that isn't there.
    if (requoteDialog === "restore") {
      setRequoteDialog(null);
      void Promise.all(
        planGroupIds.map((id) =>
          restoreProposal.act(id, (restored) =>
            restored.isActiveVersion
              ? `${subject} has been restored.`
              : `${subject} has been restored to Quote History, because a newer quote replaced it.`,
          ),
        ),
      );

      return;
    }

    const handler = {
      replace: replaceProposal,
      disregard: disregardProposal,
      promote: promoteProposal,
    }[requoteDialog];
    const verb = {
      replace: "replaced",
      disregard: "disregarded",
      promote: "replaced",
    }[requoteDialog];
    setRequoteDialog(null);
    // Act on this column's own plan group(s), not the shared proposal id: a
    // document-ingested proposal can bundle several coverage types (e.g. Vol
    // Life, STD, LTD parsed from one upload), each rendered as its own
    // CarrierGroupHeader with its own planGroupIds. Scoping to planGroupIds is
    // what keeps Replace/Disregard/Restore on this column from affecting a
    // different coverage type's quote (ADR-031, CD-1792 follow-up). Do not wrap
    // this in a swallowing .catch() — the mutation runs zod response
    // validation, and a rejected parse (spec drift) must stay visible, not hidden.
    // `onConfirm` isn't awaited by its caller, so this is deliberately
    // fire-and-forget — `void` makes that explicit rather than leaving a
    // floating promise.
    void Promise.all(
      planGroupIds.map((id) =>
        handler.act(id, `${subject} has been successfully ${verb}.`),
      ),
    );
  }, [
    requoteDialog,
    replaceProposal,
    disregardProposal,
    promoteProposal,
    restoreProposal,
    planGroupIds,
    carrierName,
    quoteVersion,
  ]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => setAnchorEl(null);

  // Dismisses the carrier's empty pad columns (CD-1612 partial fill): marks
  // each under-quoted group padSuppressed so read-time padding stops.
  const onDismissPads = useCallback(async () => {
    try {
      await Promise.all(
        padDismissGroupIds.map((id) => suppressPadSlots.mutateAsync({ id })),
      );
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
      handleClose();
    } catch {
      // no need to do anything here since the error is already handled
      // within the mutation query
    }
  }, [padDismissGroupIds, queryClient, rfpId, suppressPadSlots]);

  // A carrier with multiple alternate plan groups shares one CarrierGroupHeader across all of
  // them (see planGroupIds). Duplicating clones the whole coverage those alternates belong to
  // into a single brand new Proposal, so the carrier gets one new, independent column mirroring
  // every alternate together — any one of planGroupIds resolves the backend to the same owning
  // coverage, so a single call suffices.
  const onDuplicate = useCallback(async () => {
    const [primaryGroupId] = planGroupIds;
    if (!primaryGroupId) return;

    try {
      logger.info(`[PlanDesigner] Duplicating plan group ${primaryGroupId}`);
      await duplicateCoverage.mutateAsync({ id: primaryGroupId });
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
      handleClose();
    } catch {
      // no need to do anything here since the error is already handled
      // within the mutation query
    }
  }, [duplicateCoverage, planGroupIds, queryClient, rfpId]);

  return (
    <div
      className="ag-header-group-cell-label"
      style={{ width: "100%", display: "flex", alignItems: "center" }}
    >
      <AgGridTooltip title={headerTooltip?.trim() || props.displayName}>
        <span
          className="ag-header-group-text"
          style={{ flex: 1, textAlign: "center" }}
        >
          {props.displayName}
        </span>
      </AgGridTooltip>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={`Menu for ${props.displayName}`}
        sx={{ padding: "2px" }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {isDeleted
          ? [
              <MenuItem
                key="restore"
                onClick={() => {
                  setRequoteDialog("restore");
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <RestoreFromTrashIcon color="action" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Restore Quote" />
              </MenuItem>,
            ]
          : isPendingRequote
            ? buildPendingRequoteMenuItems({
                onReplace: () => {
                  setRequoteDialog("replace");
                  handleClose();
                },
                onDisregard: () => {
                  setRequoteDialog("disregard");
                  handleClose();
                },
              })
            : isHistorical
              ? [
                  <MenuItem
                    key="promote"
                    onClick={() => {
                      setRequoteDialog("promote");
                      handleClose();
                    }}
                  >
                    <ListItemIcon>
                      <SwapHorizIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Replace the Active Quote" />
                  </MenuItem>,
                ]
              : [
                  moveTarget && onMovePlan ? (
                    <MenuItem
                      key="move"
                      onClick={() => {
                        onMovePlan(moveTarget);
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <SyncAltIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Move Plan" />
                    </MenuItem>
                  ) : null,
                  ...pasteTargets.map((target) => (
                    <MenuItem
                      key={`paste-${target.planGroupId}`}
                      onClick={() => {
                        onPastePlan?.(
                          target.planGroupId,
                          target.targetPosition,
                        );
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <ContentPasteIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Paste Plan into Plan Group" />
                    </MenuItem>
                  )),
                  <MenuItem
                    key="duplicate"
                    onClick={onDuplicate}
                    disabled={planGroupIds.length === 0}
                  >
                    <ListItemIcon>
                      <ContentCopyIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Duplicate Plan Group" />
                  </MenuItem>,
                  padDismissGroupIds.length > 0 ? (
                    <MenuItem key="dismiss-pads" onClick={onDismissPads}>
                      <ListItemIcon>
                        <PlaylistRemoveIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Dismiss Empty Plan Column(s)" />
                    </MenuItem>
                  ) : null,
                  !isSingularPlan ? (
                    <MenuItem
                      key="delete"
                      onClick={() => {
                        setOpenDeleteDialog(true);
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <DeleteForeverOutlinedIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Delete Plan Group"
                        slotProps={{ primary: { color: "error" } }}
                      />
                    </MenuItem>
                  ) : null,
                ]}
      </Menu>

      <QuotesSbsDeletePlanGroupDialog
        planGroupIds={planGroupIds}
        placeholderDismissTargets={placeholderDismissTargets}
        carrierName={carrierName}
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        rfpId={rfpId}
      />

      {(isPendingRequote || isHistorical || isDeleted) && (
        <RequoteActionDialog
          action={requoteDialog}
          onConfirm={onRequoteConfirm}
          onCancel={() => setRequoteDialog(null)}
        />
      )}
    </div>
  );
};

CarrierGroupHeader.displayName = "CarrierGroupHeader";
export default CarrierGroupHeader;
